import { Input } from "@material-tailwind/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetEmailErrorKey } from "../../app/validation";

/**
 * Resolves the email error for a value into a ready-to-render message.
 * Pages call this from their submit handler so submit-time errors read exactly
 * the same as the on-blur ones the field produces itself.
 * Returns undefined when the address is valid.
 */
export function useEmailErrorMessage() {
    const { t } = useTranslation();
    return (email: string | undefined | null): string | undefined => {
        const key = GetEmailErrorKey(email);
        return key === undefined ? undefined : t(key);
    };
}

interface EmailFieldProps {
    /** Rendered value. The parent always receives the trimmed address. */
    value?: string;
    /** Called on every edit with the trimmed address. Does NOT validate. */
    onValueChange: (email: string) => void;
    /**
     * Error owned by the parent: a submit-time failure or a 400 from the API.
     * Takes precedence over the field's own on-blur error.
     */
    errorMessage?: string;
    /** Called when the user edits, so the parent can clear errorMessage. */
    onErrorClear?: () => void;
    id?: string;
    label?: string;
    className?: string;
    labelProps?: object;
    color?: "orange";
    disabled?: boolean;
    readOnly?: boolean;
    autoComplete?: string;
}

/**
 * The single email input used by every email field in the app.
 *
 * Behaviour, deliberate and consistent everywhere:
 *  - trims before validating, so a pasted address with a trailing newline works
 *  - validates on blur and on submit, never on keystroke (typing a valid
 *    address passes through invalid intermediate states)
 *  - clears the error as soon as the user edits the field
 *  - never blocks paste
 *  - says what to fix rather than just "invalid"
 *  - sets aria-invalid, links the message with aria-describedby, and announces
 *    it via role="alert" rather than only showing it visually
 */
export function EmailField({
    value,
    onValueChange,
    errorMessage,
    onErrorClear,
    id = "email",
    label,
    className = "w-full h-[40px]",
    labelProps = { className: "!text-[#333333] peer-focus:!text-[#F97316]" },
    color = "orange",
    disabled,
    readOnly,
    autoComplete = "email",
}: EmailFieldProps) {
    const { t } = useTranslation();
    const getEmailError = useEmailErrorMessage();

    // Raw text as typed, so the caret is never moved while the user is typing.
    // The parent only ever sees the trimmed value.
    const [text, setText] = useState(value ?? "");
    const [blurErrorMessage, setBlurErrorMessage] = useState<string | undefined>();

    // Adopt externally-driven changes (e.g. a record loaded from the API)
    // without clobbering what the user is currently typing.
    const textRef = useRef(text);
    textRef.current = text;
    useEffect(() => {
        if (value !== undefined && value !== textRef.current.trim()) {
            setText(value);
        }
    }, [value]);

    const handleChange = (raw: string) => {
        setText(raw);
        // Clear the error as soon as the user edits — both ours and the parent's.
        setBlurErrorMessage(undefined);
        onErrorClear?.();
        onValueChange(raw.trim());
    };

    const handleBlur = () => {
        const trimmed = text.trim();
        // Show the normalised address, so what is validated is what is seen.
        if (trimmed !== text) {
            setText(trimmed);
            onValueChange(trimmed);
        }
        // An empty field on blur is not yet an error — the user may not have
        // reached it. Submit validation covers the required case.
        setBlurErrorMessage(trimmed === "" ? undefined : getEmailError(trimmed));
    };

    // Parent errors (submit-time, or a 400 from the API) win over our own.
    const message = errorMessage ?? blurErrorMessage;
    const hasError = message !== undefined && message !== "";
    const messageId = `${id}-error`;

    return (
        <>
            <Input
                id={id}
                type="email"
                aria-label={label ?? t("Email")}
                label={label ?? t("Email")}
                className={className}
                labelProps={labelProps}
                color={color}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                disabled={disabled}
                readOnly={readOnly}
                autoComplete={autoComplete}
                error={hasError}
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? messageId : undefined}
                crossOrigin={undefined}
            />
            {/* role="alert" carries an implicit assertive live region, so the
                message is announced when it appears rather than only shown.
                aria-live is stated explicitly for older screen readers. */}
            {hasError ? (
                <p
                    id={messageId}
                    role="alert"
                    aria-live="assertive"
                    className="mt-1 px-3 text-xs 2xl:text-sm text-[#B91C1C]"
                >
                    {message}
                </p>
            ) : null}
        </>
    );
}

export default EmailField;
